import { z } from 'zod';

/**
 * Kısıtlama ve envanter filtresi kurallarının MEVCUT ürüne ne yapacağı (22 Ekim 2026).
 *
 * **Neden var.** SellerFlash'ın "Filtrelerim" sayfasında her koşul için üç seçenek var:
 * listelensin, stoğu 0 olarak listelensin, envanterden kaldırılsın. Bizde kısıtlama
 * taraması (`kisitlama-taramasi.service.ts`) eşleşen her ürünü tek yolla kapatıyordu:
 * pasif + stok 0, kural kalkınca geri açılıyor. Sahip: "Her risk kuralı için dokunma /
 * stok 0 / kaldır seçimi … ekleyelim".
 *
 * ⚠️ **Varsayılan STOK_0 — bugünkü davranış.** Ayarda yazılmayan kural, bozuk değer ya da
 * bilinmeyen anahtar STOK_0 sayılıyor. Yeni kolon `{}` ile geldiği için göçten sonra hiçbir
 * mağazanın davranışı değişmiyor.
 *
 * ⚠️ **DOKUNMA yeni ürünü serbest bırakmıyor.** Mevcut ilan satışta kalıyor, ama keşif ve
 * doğrudan ekleme kısıtlı ürünü yine eklemiyor (`urunKisitliMi`). Kısıtlama listesine bir
 * marka eklemek "bu markadan yeni ürün istemiyorum" demek; mevcutların ne olacağı ayrı soru.
 */

export const KURAL_ANAHTARLARI = [
  'KISIT_URUN',
  'KISIT_MARKA',
  'KISIT_KATEGORI',
  'KISIT_KELIME',
  'FILTRE_PRIME',
  'FILTRE_FBA',
  'FILTRE_FBM',
  'FILTRE_CINLI',
  'FILTRE_HEDEF_FBA',
  'FILTRE_SATICI_SAYISI',
] as const;
export type KuralAnahtari = (typeof KURAL_ANAHTARLARI)[number];

export const KURAL_EYLEMLERI = ['DOKUNMA', 'STOK_0', 'KALDIR'] as const;
export const kuralEylemiSchema = z.enum(KURAL_EYLEMLERI);
export type KuralEylemi = z.infer<typeof kuralEylemiSchema>;

export const VARSAYILAN_KURAL_EYLEMI: KuralEylemi = 'STOK_0';

/** "Amazon'dan kaldır" seçilen kurala takılan ürün bu kadar bekledikten sonra siliniyor. */
export const KALDIRMA_BEKLEME_SAAT = 24;
/** Amazon'un bu kadar kez reddettiği ürün bırakılıyor; ekranda "silinemedi" görünüyor. */
export const KALDIRMA_AZAMI_DENEME = 5;

/** Ekrandaki ad ve açıklama — kurallar ekranda bu sırayla, iki grupta gösteriliyor. */
export const KURAL_TANIMLARI: Record<
  KuralAnahtari,
  { grup: 'KISIT' | 'FILTRE'; ad: string; aciklama: string }
> = {
  KISIT_URUN: {
    grup: 'KISIT',
    ad: 'Yasaklı ürün (ASIN)',
    aciklama: 'Ürün Kısıtlamaları listesindeki ASIN',
  },
  KISIT_MARKA: { grup: 'KISIT', ad: 'Yasaklı marka', aciklama: 'Marka Kısıtlamaları listesi' },
  KISIT_KATEGORI: {
    grup: 'KISIT',
    ad: 'Yasaklı kategori',
    aciklama: 'Kategori Kısıtlamaları listesi',
  },
  KISIT_KELIME: {
    grup: 'KISIT',
    ad: 'Yasaklı kelime',
    aciklama: 'Başlıkta Kelime Kısıtlamaları listesindeki bir kelime',
  },
  FILTRE_PRIME: {
    grup: 'FILTRE',
    ad: 'Kaynakta Prime satıcı',
    aciklama: '"Prime ürünleri" kutusu açıkken',
  },
  FILTRE_FBA: {
    grup: 'FILTRE',
    ad: 'Kaynakta FBA satıcı',
    aciklama: '"FBA ürünleri" kutusu açıkken',
  },
  FILTRE_FBM: {
    grup: 'FILTRE',
    ad: 'Kaynakta FBM satıcı',
    aciklama: '"FBM ürünleri" kutusu açıkken',
  },
  FILTRE_CINLI: {
    grup: 'FILTRE',
    ad: 'Kaynakta Çinli satıcı',
    aciklama: '"Çinli satıcı" kutusu açıkken',
  },
  FILTRE_HEDEF_FBA: {
    grup: 'FILTRE',
    ad: 'Satış pazarında FBA rakip',
    aciklama: '"FBA satıcı varsa" kutusu açıkken',
  },
  FILTRE_SATICI_SAYISI: {
    grup: 'FILTRE',
    ad: 'Satıcı sayısı aralık dışında',
    aciklama: 'Satıcı sayısı aralığı değiştirilmişken (0–10 “sınır yok” demek)',
  },
};

export const KURAL_EYLEMI_TANIMLARI: Record<KuralEylemi, { ad: string; aciklama: string }> = {
  DOKUNMA: {
    ad: 'Dokunma',
    aciklama: 'Mevcut ürün satışta kalır. Yeni ürün yine eklenmez.',
  },
  STOK_0: {
    ad: 'Stok 0',
    aciklama: 'Ürün pasife alınır, Amazon’da stok 0 olur. Kural kalkınca kendiliğinden açılır.',
  },
  KALDIR: {
    ad: 'Amazon’dan kaldır',
    aciklama:
      `Ürün hemen stok 0 olur; ${KALDIRMA_BEKLEME_SAAT} saat sonra hâlâ kurala takılıyorsa ` +
      'Amazon’dan ve envanterden silinir (aynı ASIN’deki ikinci ilanlarla birlikte). Geri alınamaz.',
  },
};

/** Ayar kaydı/güncelleme şeması — her anahtar isteğe bağlı, bilinmeyen anahtar reddediliyor. */
export const kuralEylemleriSchema = z
  .object({
    KISIT_URUN: kuralEylemiSchema.optional(),
    KISIT_MARKA: kuralEylemiSchema.optional(),
    KISIT_KATEGORI: kuralEylemiSchema.optional(),
    KISIT_KELIME: kuralEylemiSchema.optional(),
    FILTRE_PRIME: kuralEylemiSchema.optional(),
    FILTRE_FBA: kuralEylemiSchema.optional(),
    FILTRE_FBM: kuralEylemiSchema.optional(),
    FILTRE_CINLI: kuralEylemiSchema.optional(),
    FILTRE_HEDEF_FBA: kuralEylemiSchema.optional(),
    FILTRE_SATICI_SAYISI: kuralEylemiSchema.optional(),
  })
  .strict();
export type KuralEylemleriGirdisi = z.infer<typeof kuralEylemleriSchema>;

/**
 * Veritabanındaki ham değer → her kural için kesin eylem.
 *
 * Bozuk/eksik değer STOK_0: bilinmeyen bir değeri "Amazon'dan kaldır" saymak geri
 * alınamaz, "dokunma" saymak yasaklı ürünü satışta bırakır. İkisinin ortası bugünkü
 * davranış.
 */
export function kuralEylemleriniCoz(ham: unknown): Record<KuralAnahtari, KuralEylemi> {
  const kaynak = ham !== null && typeof ham === 'object' && !Array.isArray(ham) ? ham : {};
  const sonuc = {} as Record<KuralAnahtari, KuralEylemi>;
  for (const anahtar of KURAL_ANAHTARLARI) {
    const deger = (kaynak as Record<string, unknown>)[anahtar];
    const cozum = kuralEylemiSchema.safeParse(deger);
    sonuc[anahtar] = cozum.success ? cozum.data : VARSAYILAN_KURAL_EYLEMI;
  }
  return sonuc;
}

/**
 * Ürünün takıldığı kuralların eylemlerinden uygulanacak olan.
 *
 * KALDIR > STOK_0; DOKUNMA hiçbir şey yapmıyor. Bir ürün "dokunma" seçilmiş markaya VE
 * "stok 0" seçilmiş kelimeye takılıyorsa stok 0 oluyor — sahibin açıkça kapattığı bir
 * kural, başka bir kuralın serbest bırakmasıyla delinmemeli.
 */
export function baskinKuralEylemi(eylemler: readonly KuralEylemi[]): 'STOK_0' | 'KALDIR' | null {
  if (eylemler.includes('KALDIR')) return 'KALDIR';
  if (eylemler.includes('STOK_0')) return 'STOK_0';
  return null;
}

/** Satıcı sayısı aralığının varsayılan üst sınırı. */
export const VARSAYILAN_SATICI_UST = 10;

/**
 * Satıcı sayısı filtresi uygulanıyor mu? Yalnız varsayılan aralık (0–10) "filtre yok" demek;
 * kullanıcının hiç dokunmadığı bir ayar yüzünden ürün kapanmasın. Tarama, önizleme ve ekran
 * aynı kuralı kullanıyor.
 */
export function saticiAraligiAcikMi(min: number, max: number): boolean {
  return min > 0 || max !== VARSAYILAN_SATICI_UST;
}

/** Filtre kurallarının uygulanıp uygulanmadığını belirleyen ayarlar. */
export interface KuralFiltreleri {
  excludePrime: boolean;
  excludeFba: boolean;
  excludeFbm: boolean;
  excludeChineseSeller: boolean;
  jpExcludeIfFba: boolean;
  jpSellerCountMin: number;
  jpSellerCountMax: number;
}

/** Kural uygulanıyor mu? Liste kuralları her zaman, filtre kuralları ilgili ayar açıkken. */
export function kuralUygulaniyorMu(kural: KuralAnahtari, f: KuralFiltreleri): boolean {
  switch (kural) {
    case 'FILTRE_PRIME':
      return f.excludePrime;
    case 'FILTRE_FBA':
      return f.excludeFba;
    case 'FILTRE_FBM':
      return f.excludeFbm;
    case 'FILTRE_CINLI':
      return f.excludeChineseSeller;
    case 'FILTRE_HEDEF_FBA':
      return f.jpExcludeIfFba;
    case 'FILTRE_SATICI_SAYISI':
      return saticiAraligiAcikMi(f.jpSellerCountMin, f.jpSellerCountMax);
    default:
      return true;
  }
}

/** Bir panelin kural eylemleri ve filtreleri. */
export interface KuralDurumu {
  eylemler: Record<KuralAnahtari, KuralEylemi>;
  filtreler: KuralFiltreleri;
}

/**
 * Bu değişiklikle "Amazon'dan kaldır" hangi kurallarda YENİ devreye giriyor: eylem yeni KALDIR
 * oldu ya da KALDIR seçili filtre yeni açıldı. Kapalı filtrenin eylemi uygulanmadığı için sayılmıyor.
 */
export function yeniKaldirKurallari(once: KuralDurumu, sonra: KuralDurumu): KuralAnahtari[] {
  const kaldiriyor = (d: KuralDurumu, k: KuralAnahtari) =>
    d.eylemler[k] === 'KALDIR' && kuralUygulaniyorMu(k, d.filtreler);
  return KURAL_ANAHTARLARI.filter((k) => kaldiriyor(sonra, k) && !kaldiriyor(once, k));
}

/** Geri alınamaz silmeyi başlatacak kuralların metni: uyarı, önizleme ve onay aynı şeyi söylüyor. */
export function kaldirmaUyarisi(
  kurallar: readonly KuralAnahtari[],
  an: 'kaydettiğiniz' | 'uyguladığınız',
): string {
  const adlar = [...new Set(kurallar)].map((k) => KURAL_TANIMLARI[k].ad).join(', ');
  return (
    `${adlar} kuralına takılan ürünler ${an} anda stok 0 olur; ${KALDIRMA_BEKLEME_SAAT} saat içinde ` +
    'vazgeçmezseniz (kuralın eylemini değiştirmezseniz) aynı ASIN’deki ikinci ilanlarla birlikte ' +
    'Amazon’dan ve envanterden silinir.'
  );
}
